import {
    Link,
    LoaderFunctionArgs,
    useLoaderData,
    useNavigate,
    useRevalidator,
} from "react-router-dom";
import { createApolloClient } from "../../providers/ApolloClientFactory";
import { GET_DISCOUNTS } from "./queries";
import { Discount } from "../../types/discount";
import { usdFormat } from "../../utils";
import { useEffect, useMemo, useRef, useState } from "react";
import { Badge } from "../../components/Badge";
import moment from "moment";
import { usePermissions } from "../auth/api";
import { organizationStore } from "../../store/organization";
import {
    ColDef,
    GridApi,
    SizeColumnsToContentStrategy,
} from "ag-grid-community";
import { Input } from "../../components/inputs/Input";
import { PrimaryButton, SecondaryButton } from "../../components/Button";
import { AgGridReact } from "ag-grid-react";
import { UPDATE_DISCOUNT } from "./mutations";
import { useGridEdit } from "../helpers/GridEditHelper";
import GridEditActions from "../helpers/GridEditActions";
import { useGridSave } from "../helpers/UseGridSave";
import { userStore } from "../../store/user";

const client = createApolloClient();
const { getState: getOrgState } = organizationStore;
const { getState: getUserStore } = userStore;

const buildSearch = (search: string) => [
    { name: { _ilike: `%${search}%` } },
    { type: { _ilike: `%${search}%` } },
    { description: { _ilike: `%${search}%` } },
    { code: { _ilike: `%${search}%` } },
];

type CellRenderer = (params: {
    value:
        | string
        | number
        | boolean
        | React.ReactElement
        | React.ReactFragment
        | React.ReactPortal
        | null
        | undefined;
    data: Discount;
}) => JSX.Element | null;

const createCellRenderer = (
    permission: boolean,
    linkFunc: (params: { data: Discount }) => string
): CellRenderer => {
    return (params) => {
        if (params.value === undefined) return null;
        return permission ? (
            <Link to={linkFunc(params)}>
                <Badge>{params.value}</Badge>
            </Link>
        ) : (
            <Badge>{params.value}</Badge>
        );
    };
};

export const discountsListLoader = async ({ request }: LoaderFunctionArgs) => {
    const url = new URL(request.url);
    const search = url.searchParams.get("search");
    const rawOrganizationId = getOrgState().organizationId;
    const organizationId = parseInt(
        rawOrganizationId !== null ? rawOrganizationId.toString() : "",
        10
    );

    let where: any = { _or: search ? buildSearch(search) : undefined };
    if (organizationId && organizationId !== 0) {
        where = {
            ...where,
            org: { id: { _in: [organizationId] } },
        };
    }

    const { data } = await client.query({
        query: GET_DISCOUNTS,
        variables: { where },
    });
    return data.discount as Discount[];
};

export const DiscountsList = () => {
    const { editCreatePermission } = usePermissions("discounts");
    const canEdit = !!editCreatePermission;

    const navigate = useNavigate();
    const revalidator = useRevalidator();
    const gridRef = useRef<GridApi>();
    const discounts = useLoaderData() as Discount[];
    const [rowData, setRowData] = useState<Discount[]>([]);

    const {
        pending,
        pendingCount,
        resetBaseline,
        makeEditable,
        makeStringToggle,
        setPending,
        getBaselineRows,
    } = useGridEdit<Discount>();

    useEffect(() => {
        setRowData(discounts);
        resetBaseline(discounts);
    }, [discounts, resetBaseline]);

    const discountTypeOptions = useMemo(
        () =>
            Array.from(
                new Set(
                    (discounts ?? [])
                        .map((d) => String(d.discount_type ?? "").trim())
                        .filter(Boolean)
                )
            ).sort((a, b) => a.localeCompare(b)),
        [discounts]
    );

    const idRenderer = useMemo(
        () => createCellRenderer(canEdit, (p) => `/discounts/${p.data.id}/edit`),
        [canEdit]
    );
    const nameRenderer = useMemo(
        () => createCellRenderer(canEdit, (p) => `/discounts/${p.data.id}/edit`),
        [canEdit]
    );

    const colDefs: ColDef<Discount>[] = useMemo(
        () => [
            {
                headerName: "Id",
                field: "id",
                cellRenderer: idRenderer,
                filter: "agTextColumnFilter",
                pinned: "left",
                editable: false,
            },
            {
                headerName: "Discount Name",
                field: "name",
                cellRenderer: nameRenderer,
                filter: "agTextColumnFilter",
                pinned: "left",
                ...makeEditable("name", { type: "text", canEdit }, setRowData),
            },
            {
                headerName: "Discount Type",
                field: "discount_type",
                ...makeStringToggle(
                    "discount_type",
                    {
                        canEdit,
                        trueValue: "amount",
                        falseValue: "percentage",
                        labels: { on: "Amount", off: "Percent" },
                        toggleOnClick: true,
                    },
                    setRowData
                ),
                filter: "agTextColumnFilter",
                filterValueGetter: (p) =>
                    String((p.data as any)?.discount_type ?? "").toLowerCase().trim() === "amount"
                        ? "Amount"
                        : "Percent",
            },
            {
                headerName: "Description",
                field: "description",
                ...makeEditable("description", { type: "text", canEdit }, setRowData),
                filter: "agTextColumnFilter",
            },
            {
                headerName: "Organization",
                field: "org.name",
                valueGetter: (p) => p.data?.org?.name,
                filter: "agTextColumnFilter",
                editable: false,
            },
            {
                headerName: "Code",
                field: "code",
                ...makeEditable(
                    "code",
                    {
                        type: "text",
                        canEdit,
                        formatter: (v: any) => String(v ?? "").trim(),
                        parser: (nv: any) => String(nv ?? "").trim(),
                        equals: (a: any, b: any) =>
                            String(a ?? "").trim() === String(b ?? "").trim(),
                    },
                    setRowData
                ),
                filter: "agTextColumnFilter",
            },
            {
                headerName: "Amount",
                field: "amount",
                ...makeEditable(
                    "amount",
                    {
                        type: "number",
                        canEdit,
                        precision: 2,
                        displayFactor: 100,
                        format: (v) => usdFormat.format(v ?? 0),
                    },
                    setRowData
                ),
                filter: "agTextColumnFilter",
                valueFormatter: (p) => usdFormat.format((p.value ?? 0) / 100),
                filterValueGetter: (p) => usdFormat.format(((p.data as any)?.amount ?? 0) / 100),
            },
            {
                headerName: "Percentage",
                field: "percentage",
                ...makeEditable(
                    "percentage",
                    { type: "number", canEdit, precision: 2 },
                    setRowData
                ),
                filter: "agTextColumnFilter",
                valueFormatter: (p) => {
                    const n = Number(p.value ?? 0);
                    return n !== 0 ? `${n.toFixed(2)}%` : "";
                },
                filterValueGetter: (p) => {
                    const n = Number((p.data as any)?.percentage ?? 0);
                    return n !== 0 ? `${n.toFixed(2)}%` : "";
                },
            },
            {
                headerName: "Updated By",
                field: "user.username",
                valueGetter: (p) => p.data?.user?.username,
                filter: "agTextColumnFilter",
                editable: false,
            },
            {
                headerName: "Created Date",
                field: "created_at",
                valueFormatter: (p) => {
                    const d = p.value ? moment(p.value) : null;
                    return d ? d.format("MM/DD/YYYY h:mm:ss a") : "";
                },
                editable: false,
                filter: "agTextColumnFilter",
                filterValueGetter: (p) =>
                    (p.data as any)?.created_at
                        ? moment((p.data as any).created_at).format("MM/DD/YYYY h:mm:ss a")
                        : "",
            },
            {
                headerName: "Updated Date",
                field: "updated_at",
                valueFormatter: (p) => {
                    const d = p.value ? moment(p.value) : null;
                    return d ? d.format("MM/DD/YYYY h:mm:ss a") : "";
                },
                editable: false,
                filter: "agTextColumnFilter",
                filterValueGetter: (p) =>
                    (p.data as any)?.updated_at
                        ? moment((p.data as any).updated_at).format("MM/DD/YYYY h:mm:ss a")
                        : "",
            },
        ],
        [canEdit, discountTypeOptions, idRenderer, nameRenderer, makeEditable, setRowData]
    );

    const rowHeight = 50;
    const autoSizeStrategy: SizeColumnsToContentStrategy = {
        type: "fitCellContents",
    };

    const handleExportCSV = () => {
        if (gridRef.current) {
            const timestamp = new Date().toISOString().replace(/[-:]/g, "");
            gridRef.current.exportDataAsCsv({
                fileName: `Discounts_${timestamp}.csv`,
                columnSeparator: ",",
            });
        }
    };

    const mutateRow = async (id: number, rawPatch: Partial<Discount>) => {
        const patch = Object.fromEntries(
            Object.entries(rawPatch as Record<string, unknown>).filter(
                ([k, v]) =>
                    ![
                        "id",
                        "user",
                        "user_id",
                        "org",
                        "org_id",
                        "created_at",
                        "updated_at",
                    ].includes(k) && v !== undefined
            )
        );

        if (Object.keys(patch).length === 0) return;

        const user = getUserStore().user;
        const data = {
            ...patch,
            last_updated_by: user?.id,
            updated_at: new Date().toISOString()
        };

        const res = await client.mutate({
            mutation: UPDATE_DISCOUNT,
            variables: { id, data },
            errorPolicy: "all",
        });

        if (res.errors?.length) {
            throw new Error(res.errors.map((e: any) => e.message).join("; "));
        }
    };

    const { handleSave } = useGridSave<Discount>({
        rowData,
        setRowData,
        pending,
        setPending,
        resetBaseline,
        gridApiRef: gridRef as any,
        revalidate: () => revalidator.revalidate(),
        mutateRow,
        optimistic: true,
    });

    const gridOptions = { enableCellTextSelection: true };

    return (
        <div className="w-full flex flex-col overflow-hidden" style={{ height: "calc(100vh - 128px)" }}>
            <div className="md:hidden grid grid-cols-6 min-w-0 px-0 py-0">
                <div className="col-span-6 flex items-center justify-between min-h-[48px] mb-1">
                    <div className="flex items-center gap-2 pl-2 pt-2">
                        <GridEditActions
                            pendingCount={pendingCount}
                            pending={pending}
                            gridRef={gridRef}
                            resetBaseline={resetBaseline}
                            baselineData={discounts}
                            getBaselineRows={getBaselineRows}
                            setRowData={setRowData}
                            setPending={setPending}
                            onSave={handleSave}
                        />
                    </div>
                </div>

                <div className="col-span-6 flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                        <div className="w-full px-3 py-2">
                            <Input
                                name="search"
                                placeholder="Search Discounts"
                                onChange={(s) => {
                                    navigate(s && typeof s === "string" && s.length > 0 ? `/discounts/list?search=${s}` : `/discounts/list`);
                                    revalidator.revalidate();
                                }}
                            />
                        </div>
                    </div>

                    <div className="flex items-center gap-1 shrink-0">
                        <SecondaryButton onClick={handleExportCSV}>Export to CSV</SecondaryButton>
                        {canEdit && (
                            <PrimaryButton onClick={() => navigate(`/discounts/create`)}>Create</PrimaryButton>
                        )}
                    </div>
                </div>
            </div>

            <div className="hidden md:grid grid-cols-12 items-center gap-3 min-w-0 py-2">
                <div className="col-span-5 min-w-0">
                    <div className="w-full px-3 py-2">
                        <Input
                            name="search"
                            placeholder="Search Discounts"
                            onChange={(s) => {
                                navigate(s && typeof s === "string" && s.length > 0 ? `/discounts/list?search=${s}` : `/discounts/list`);
                                revalidator.revalidate();
                            }}
                        />
                    </div>
                </div>

                <div className="col-span-3 col-start-6 justify-self-start flex items-center gap-1">
                    <GridEditActions
                        pendingCount={pendingCount}
                        pending={pending}
                        gridRef={gridRef}
                        resetBaseline={resetBaseline}
                        baselineData={discounts}
                        getBaselineRows={getBaselineRows}
                        setRowData={setRowData}
                        setPending={setPending}
                        onSave={handleSave}
                    />
                </div>

                <div className="col-span-4 flex justify-end items-center gap-1 whitespace-nowrap">
                    <SecondaryButton onClick={handleExportCSV}>
                        <span className="whitespace-nowrap">Export&nbsp;to&nbsp;CSV</span>
                    </SecondaryButton>
                    {canEdit && (
                        <PrimaryButton onClick={() => navigate(`/discounts/create`)}>Create</PrimaryButton>
                    )}
                </div>
            </div>

            <div className="flex-1 min-h-0">
                <div className="ag-theme-quartz w-full h-full min-w-0">
                    <AgGridReact
                        rowData={rowData}
                        columnDefs={colDefs}
                        rowHeight={rowHeight}
                        autoSizeStrategy={autoSizeStrategy}
                        pagination
                        gridOptions={gridOptions}
                        paginationPageSize={100}
                        onGridReady={(params) => (gridRef.current = params.api)}
                        singleClickEdit
                        stopEditingWhenCellsLoseFocus
                    />
                </div>
            </div>
        </div>
    );
};
